import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Button } from '@/deploy/components/ui/Button';
import { Icon } from '@/deploy/components/ui/Icon';
import { networkAdminService } from '@/deploy/services/network.service';
import { useSessionStore } from '@/deploy/stores/session.store';
import type { CloudflareZone, NetworkSettings } from '@/deploy/types/network';
import { useNotificationsStore } from '@/stores/notifications.store';
import { errorMessage } from '../networkFormat';
import styles from '../Network.module.scss';

/**
 * Projet par défaut et domaines principaux de chaque projet. Un vhost ou un certificat va au projet
 * dont un domaine principal le contient (le plus long gagne), sinon au projet par défaut.
 */
export function ProjectDomainsCard({ zones }: { zones: CloudflareZone[] }) {
  const projects = useSessionStore((s) => s.projects);
  const notify = useNotificationsStore();
  const [settings, setSettings] = useState<NetworkSettings | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    networkAdminService.getSettings().then(setSettings).catch((e) => setError(errorMessage(e)));
  }, []);

  const claimed = useMemo(() => new Set(Object.values(settings?.projectDomains ?? {}).flat()), [settings]);
  const unclaimedZones = zones.filter((z) => !claimed.has(z.name));

  if (!settings) {
    return (
      <div className={styles.cardWide}>
        <Head />
        {error ? <div className={styles.error}>{error}</div> : <span className={styles.muted}>Chargement…</span>}
      </div>
    );
  }

  const domainsOf = (projectId: string) => settings.projectDomains[projectId] ?? [];
  const setDomains = (projectId: string, list: string[]) =>
    setSettings({ ...settings, projectDomains: { ...settings.projectDomains, [projectId]: list } });

  const add = (projectId: string) => {
    const value = (drafts[projectId] ?? '').trim().toLowerCase();
    if (!value) return;
    if (!domainsOf(projectId).includes(value)) setDomains(projectId, [...domainsOf(projectId), value]);
    setDrafts({ ...drafts, [projectId]: '' });
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      setSettings(await networkAdminService.saveSettings(settings));
      notify.success('Réglages réseau enregistrés');
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.cardWide}>
      <Head action={<Button variant="primary" size="sm" onClick={save} loading={saving}>Enregistrer</Button>} />
      <span className={styles.hint}>
        Un domaine appartient au projet dont un domaine principal le contient (le plus précis gagne,
        ex : <code>be.recube.gg</code> avant <code>recube.gg</code>). Tout le reste va au projet par défaut.
      </span>

      <label className={styles.field} style={{ maxWidth: 360 }}>
        <span>Projet par défaut</span>
        <select value={settings.defaultProjectId ?? ''} onChange={(e) => setSettings({ ...settings, defaultProjectId: e.target.value || null })}>
          <option value="">Aucun (les domaines non attribués sont masqués)</option>
          {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </label>

      <datalist id="network-zones">
        {unclaimedZones.map((z) => <option key={z.name} value={z.name}>{z.account}</option>)}
      </datalist>

      {projects.map((p) => (
        <div key={p.id} className={styles.projectRow}>
          <div className={styles.projectName}>
            <span className={styles.projectChip} style={{ background: p.color }}>{p.code}</span>
            {p.name}
          </div>
          <div className={styles.chips}>
            {domainsOf(p.id).map((d) => (
              <span key={d} className={styles.chip}>
                {d}
                <button type="button" aria-label={`Retirer ${d}`} onClick={() => setDomains(p.id, domainsOf(p.id).filter((x) => x !== d))}>
                  <Icon name="x" size={10} />
                </button>
              </span>
            ))}
            <input list="network-zones" placeholder={p.id === settings.defaultProjectId ? 'défaut : reçoit le reste' : 'ajouter un domaine'}
              value={drafts[p.id] ?? ''}
              onChange={(e) => setDrafts({ ...drafts, [p.id]: e.target.value })}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(p.id); } }}
              onBlur={() => add(p.id)} />
          </div>
        </div>
      ))}

      {unclaimedZones.length > 0 && (
        <span className={styles.hint}>
          Zones Cloudflare non attribuées ({settings.defaultProjectId ? 'projet par défaut' : 'masquées'}) :
          {' '}{unclaimedZones.map((z) => z.name).join(', ')}
        </span>
      )}
      {error && <div className={styles.error}>{error}</div>}
    </div>
  );
}

function Head({ action }: { action?: ReactNode }) {
  return (
    <div className={styles.cardHead}>
      <h3><Icon name="layers" size={16} /> Projets et domaines principaux</h3>
      <span className={styles.spacer} />
      {action}
    </div>
  );
}
